import { useLingui } from '@lingui/react/macro';
import { Command } from 'cmdk';
import { Check, ChevronDown, X } from 'lucide-react';
import { useId, useState } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { cn } from '@/shared/ui/cn';
import type { FieldControlProps } from '@/shared/ui/field';
import { fieldControlClass } from '@/shared/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';

export interface MultiOption {
  readonly value: string;
  readonly label: string;
}

interface MultiSelectProps {
  readonly control: FieldControlProps;
  readonly options: readonly MultiOption[];
  readonly value: readonly string[];
  readonly onChange: (value: string[]) => void;
  readonly onBlur: () => void;
  readonly placeholder?: string;
  /** Nome da lista aberta (o rótulo do campo). */
  readonly label?: string;
}

/**
 * Escolha múltipla pelo valor (os motivos de `GET /RejectionReason`, a blacklist). Web: lista com
 * busca num popover, escolhidos como etiquetas no campo; iPhone: etiquetas que ligam e desligam,
 * sem popover sobre o sheet (protótipo `m_filters`).
 */
export function MultiSelect({
  control,
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  label,
}: MultiSelectProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const selected = new Set(value);
  const toggle = (option: string) =>
    onChange(selected.has(option) ? value.filter((v) => v !== option) : [...value, option]);
  const aria = {
    'aria-labelledby': control['aria-labelledby'],
    ...(control['aria-describedby'] ? { 'aria-describedby': control['aria-describedby'] } : {}),
    ...(control['aria-invalid'] ? { 'aria-invalid': true } : {}),
  };

  if (isMobile) {
    return (
      <div id={control.id} role="group" {...aria} className="flex flex-wrap gap-2" onBlur={onBlur}>
        {options.map((option) => {
          const on = selected.has(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={on}
              disabled={control.disabled || control.readOnly}
              onClick={() => toggle(option.value)}
              className={cn(
                'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium',
                on
                  ? 'border-accent-line bg-accent-wash text-accent-text'
                  : 'border-input bg-card text-foreground',
              )}
            >
              {on ? <Check aria-hidden className="size-4" /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  const chosen = options.filter((option) => selected.has(option.value));
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={control.id}
          aria-expanded={open}
          aria-controls={listId}
          disabled={control.disabled || control.readOnly}
          {...aria}
          {...(control.required ? { 'aria-required': true } : {})}
          className={cn(
            fieldControlClass,
            'h-auto min-h-control justify-between gap-2 py-1 text-left',
          )}
        >
          {chosen.length === 0 ? (
            <span className="truncate text-muted-foreground">{placeholder ?? t`Selecione`}</span>
          ) : (
            <span className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {chosen.map((option) => (
                <span
                  key={option.value}
                  className="inline-flex h-6 max-w-full items-center rounded-full bg-neutral-wash px-2 text-xs font-medium text-neutral"
                >
                  <span className="truncate">{option.label}</span>
                </span>
              ))}
            </span>
          )}
          <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        {...(label ? { 'aria-label': label } : {})}
        className="w-(--radix-popover-trigger-width) min-w-64 p-0"
      >
        <Command loop className="flex flex-col">
          {options.length > 7 ? (
            <Command.Input
              placeholder={t`Buscar…`}
              className="h-control w-full border-b border-border bg-transparent px-3 text-field outline-none placeholder:text-faint"
            />
          ) : null}
          <Command.List id={listId} aria-multiselectable className="max-h-72 overflow-y-auto p-1">
            <Command.Empty className="px-2 py-3 text-center text-sm text-muted-foreground">{t`Nenhum resultado encontrado`}</Command.Empty>
            {options.map((option) => (
              <Command.Item
                key={option.value}
                value={option.value}
                keywords={[option.label]}
                aria-selected={selected.has(option.value)}
                onSelect={() => toggle(option.value)}
                className="relative flex min-h-8 items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-control outline-none select-none data-[selected=true]:bg-accent-wash data-[selected=true]:text-accent-text"
              >
                <span className="truncate">{option.label}</span>
                {selected.has(option.value) ? (
                  <Check aria-hidden className="absolute right-2 size-4 text-accent-text" />
                ) : null}
              </Command.Item>
            ))}
          </Command.List>
          {chosen.length > 0 ? (
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex h-9 items-center justify-center gap-1.5 border-t border-border text-sm text-muted-foreground hover:text-foreground"
            >
              <X aria-hidden className="size-3.5" />
              {t`Limpar seleção`}
            </button>
          ) : null}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
